import { useMutation } from '@apollo/client';
import { LoadingButton } from '@mui/lab';
import { Button } from '@mui/material';
import { GridFilterModel, GridRenderCellParams } from '@mui/x-data-grid-premium';
import { GridRowModel } from '@mui/x-data-grid-pro';
import { cloneDeep } from 'lodash';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
    autoCreateQuery as autoCreateOrganizationQuery,
    client as organizationClient,
    useCreateOrganizationWithInput
} from '../../../../src/organization/organization';
import { V } from '../../../common/Layout';
import { useStyling } from '../../../common/Theme';
import { SECURITY_ACTIONS, useSecurity } from '../../../common/auth/Security';
import { UserContext } from '../../../common/auth/UserContext';
import { createTermMutation, metadataClient } from '../../../common/components/MetadataAutocomplete';
import { useGetPersonDetailsLazily } from '../../../common/components/person/useGetPersonDetailLazily';
import { Contact } from '../../../common/components/types';
import { FILTER_IDENTIFIERS } from '../../../common/constants/constants';
import { validateEmail } from '../../../common/form/Validator';
import { useMutationWithContext } from '../../../common/hooks/useMutationWithContext';
import I18n from '../../../common/i18n/I18n';
import { Icons } from '../../../common/icons/Icons';
import DataGrid from '../../../common/list/DataGrid';
import { RowAction } from '../../../common/list/DataGrid/types';
import TooltipRenderer from '../../../common/list/TooltipRenderer';
import { getReferenceId } from '../../../common/reference/reference';
import {
    autoCreateQuery as autoCreateProductionQuery,
    client as productionClient
} from '../../../production/helpers/production';
import { getColumns } from '../../reservation/ContactListUtils';
import ContactEditor from './ContactEditor';
import { contactsToRows, linkedPeopleToRows, openMarketplaceAndFetchPerson, rowToContact } from './ContactListUtils';

export default function ContactList({
    feature,
    contacts,
    onAddContact = undefined,
    onEditContact = undefined,
    onDeleteContact,
    onSaveContacts = undefined,
    title = undefined,
    addTitle,
    sx = {},
    exportFilename,
    enableImport = false,
    setImportVisible = (importVisible, isUpload) => {},
    errors = {},
    isSaving = false,
    showSaveButton = false,
    colDefs,
    columnVisibility = {},
    onError = undefined,
    doUpdate = undefined,
    errorInRow = undefined,
    setIsRowUpdated = undefined,
    isRowUpdated = false,
    isOpenConfirmationModal = false,
    autoCreate = false,
    toolbar,
    setIsMarketplaceOpen = undefined,
    handleItemClick = undefined,
    externalAddTriggerValue = undefined,
    onCreateNewRow = undefined,
    pendingImportRowIds = [],
    clickableColumn = FILTER_IDENTIFIERS.firstName,
    filterModel = undefined,
    dataGridId = 'contact-list',
    isContactMode = true,
    disableFilterOptions = false,
    entityName = undefined,
    ...rest
}) {
    const { theme } = useStyling();
    const { activeOrganizationAccount } = useContext(UserContext);
    const { allowed: canEditBySecurity } = useSecurity(SECURITY_ACTIONS.EDIT, {
        featureName: feature,
        entityName
    });
    const canOpenContactEditor = Boolean(onEditContact && canEditBySecurity);
    const [contactToEdit, setContactToEdit] = useState<any>(undefined);
    const [contactEditorVisible, setContactEditorVisible] = useState(false);
    const [saving, setSaving] = useState(false);
    const [rows, setRows] = useState<GridRowModel[]>([]);
    const [errorInEmail, setErrorInEmail] = useState({ error: false, errorMessage: '' });
    const [currentRow, setCurrentRow] = useState(undefined);
    const { getPersonDetail } = useGetPersonDetailsLazily();
    const [saveProduction] = useMutationWithContext(autoCreateProductionQuery, productionClient);
    const [saveOrganization] = useMutationWithContext(autoCreateOrganizationQuery, organizationClient);
    const [createTerm] = useMutation(createTermMutation, {
        client: metadataClient,
        context: {
            headers: {
                ownerId: activeOrganizationAccount
            }
        },
        notifyOnNetworkStatusChange: true
    });
    const mutationObject = { production: saveProduction, organization: saveOrganization, createTerm: createTerm };
    const getNonFuzzyQueryParamsForEmail = (email: string) => {
        let query = {};
        if (validateEmail(email)) {
            query = {
                field: 'email',
                value: email
            };
        }
        return query;
    };

    useEffect(() => {
        if (errorInEmail && onError) {
            onError(errorInEmail.error);
        }
    }, [errorInEmail.error]);

    /**
     * Validates email for duplicates after API search or field update.
     * Checks against both API results and existing grid rows.
     *
     * Business Logic:
     * - Scenario 1: Single API match + no personId → Auto-select
     * - Scenario 2: Multiple API matches → Show error
     * - Scenario 3: Grid duplicates → Show error
     *
     * @param items - API search results (Person entities)
     * @param inputValue - Email address being validated
     * @param currentEditRows - Current editing state from DataGrid
     * @param id - Row ID being edited
     * @returns Error message component if duplicate/invalid, undefined otherwise
     */
    const afterSearch = useCallback(
        (items, inputValue, currentEditRows, id) => {
            if (!isContactMode) {
                return;
            }
            let message;
            const editRow = currentEditRows?.[id] ?? currentEditRows?.[String(id)];
            if (!editRow) {
                return;
            }
            const refVal = editRow.personReference?.value ?? editRow.reference;
            const personId = getReferenceId(refVal);
            const currentRowId = String(id);

            const normalizedInputEmail = String(inputValue || '')
                .trim()
                .toLowerCase();
            const isValid = validateEmail(inputValue);

            // CHECK 1: API Results
            // Special case: Single API match with no person selected = auto-select
            const filteredApiItems = items.filter((item) => {
                if (!personId) {
                    return true; // Include all items for checking
                }
                // Exclude current person when editing
                return String(item?.id) !== String(personId);
            });

            const foundDuplicateInApi = filteredApiItems.some((item) => {
                const itemEmail = String(item?.contactInfo?.email?.address || '')
                    .trim()
                    .toLowerCase();
                return itemEmail !== '' && itemEmail === normalizedInputEmail;
            });

            // CHECK 2: Grid Rows
            // Check existing rows in the booking for duplicate emails
            const filteredGridRows = rows.filter((row) => {
                // Exclude current row being edited
                if (String(row.id) === currentRowId) {
                    return false;
                }

                // Exclude same person if already selected
                if (personId) {
                    const rowPersonRef = row?.personReference ?? row?._raw?.personReference;
                    const rowPersonId = getReferenceId(rowPersonRef);
                    if (rowPersonId && String(rowPersonId) === String(personId)) {
                        return false;
                    }
                }

                return true;
            });

            const foundDuplicateInGrid = filteredGridRows.some((row) => {
                const rowEmail = String(
                    row?.contactInfo?.email?.address || row?._raw?.contactInfo?.email?.address || ''
                )
                    .trim()
                    .toLowerCase();
                return rowEmail !== '' && rowEmail === normalizedInputEmail;
            });

            // Combine both checks - error if duplicate found in either source
            const foundDuplicateEmail = foundDuplicateInApi || foundDuplicateInGrid;

            if (foundDuplicateEmail) {
                message = <I18n token="reservation.guest.duplicate.email.error.message" />;
                setErrorInEmail({ error: true, errorMessage: message });
            } else if (!isValid) {
                message = <I18n token="reservation.guest.invalid.email.error.message" />;
                setErrorInEmail({ error: true, errorMessage: message });
            } else {
                setErrorInEmail({ error: false, errorMessage: '' });
            }

            return message;
        },
        [rows]
    );

    const handleRowEditStop = useCallback(() => {
        setErrorInEmail({ error: false, errorMessage: '' });
    }, []);
   
    const filterSelectedOptions = useCallback(
        (options: Contact[], currentRowId: string | number) => {
            const selectedRefs = new Set(
                rows
                    .filter((row) => String(row.id) !== String(currentRowId))
                    .map((row) => {
                        const ref = row?.personReference ?? row?._raw?.personReference;
                        return getReferenceId(ref);
                    })
                    .filter(Boolean)
                    .map(String)
            );
            if (selectedRefs.size === 0) return options;
            return options.filter((opt) => {
                const optId = String(getReferenceId(opt?.personReference) ?? opt?.id);
                return !optId || !selectedRefs.has(optId);
            });
        },
        [rows]
    );

    const createNewOrg = useCreateOrganizationWithInput(saveOrganization);

    const allColumns = useMemo(
        () =>
            getColumns(
                colDefs,
                setIsRowUpdated,
                afterSearch,
                getNonFuzzyQueryParamsForEmail,
                mutationObject,
                autoCreate,
                createNewOrg,
                filterSelectedOptions,
                disableFilterOptions
            ),
        [
            colDefs,
            setIsRowUpdated,
            afterSearch,
            getNonFuzzyQueryParamsForEmail,
            mutationObject,
            autoCreate,
            createNewOrg,
            filterSelectedOptions,
            disableFilterOptions
        ]
    );

    const updatedColumns = useMemo(
        () =>
            allColumns.map((col) => {
                if (errorInRow?.field === col.field) {
                    return {
                        ...col,
                        renderCell: (params: GridRenderCellParams) => {
                            const id = getRowIdentifier(params.row);
                            const openTooltip = !!id && errorInRow?.id === id;
                            const isOpen = openTooltip ? true : false;
                            return <TooltipRenderer {...params} isOpen={isOpen} message={errorInRow?.message || ''} />;
                        }
                    };
                }
                return col;
            }),
        [allColumns, errorInRow]
    );

    useEffect(() => {
        setRows(isContactMode ? contactsToRows(contacts, allColumns) : linkedPeopleToRows(contacts, allColumns));
    }, [contacts, isContactMode]);

    const handleRowAction = (action: RowAction, row, allRows) => {
        const updatedContact: Contact = rowToContact(row, colDefs);
        const updatedContacts: Contact[] = allRows.map((r) => rowToContact(r, colDefs));
        if (action === RowAction.Add) {
            return onAddContact(updatedContact, updatedContacts);
        } else if (action === RowAction.Edit) {
            return onEditContact(updatedContact, updatedContacts);
        } else if (action === RowAction.Delete) {
            return onDeleteContact(updatedContact, updatedContacts);
        }
    };

    const processRowUpdate = async (update: GridRowModel) => {
        const contactId = getRowIdentifier(update) ?? String(update.id);
        const addNewGuest = !!errorInRow && errorInRow.id !== contactId;
        return new Promise((resolve, reject) => {
            // If there's an email validation error, keep the row in edit mode
            // by rejecting with the original row data
            if (errorInEmail?.error || addNewGuest || isOpenConfirmationModal) {
                reject(update); // Pass the current data back to keep it in edit mode
                return;
            }

            const isNew = update.isNew;
            const updatedRow = { ...update, isNew: false };
            let newRows: GridRowModel[] = rows.map((r) => (r.id === update.id ? updatedRow : r));

            if (!showSaveButton) {
                handleRowAction(isNew ? RowAction.Add : RowAction.Edit, updatedRow, newRows);
            }

            setRows(newRows);

            const hasChangesInTable = isRowUpdated || isNew;
            const updateGuest = doUpdate && !isOpenConfirmationModal && hasChangesInTable;

            if (updateGuest) {
                const contactToUpdate = rowToContact(cloneDeep(updatedRow), colDefs);
                setCurrentRow(contactToUpdate);
                doUpdate(contactToUpdate, isNew);
            }

            resolve(updatedRow);
        });
    };

    const uploadButton = (
        <Button
            key={'import'}
            startIcon={<Icons.FileUpload />}
            size={'small'}
            onClick={() => setImportVisible(true, true)}
        >
            Import
        </Button>
    );

    const saveButton = (
        <LoadingButton
            loading={isSaving}
            key={'save'}
            size={'small'}
            color={'primary'}
            startIcon={<Icons.Save />}
            disabled={errorInEmail.error || rows?.length === 0}
            onClick={() => {
                const updatedContacts: Contact[] = rows.map((r) => rowToContact(r, colDefs));
                return onSaveContacts(updatedContacts);
            }}
        >
            Save
        </LoadingButton>
    );

    const isCellClickable = ({ row, field }) =>
        field === 'fullName' || field === 'name.firstName' || field === 'name.lastName';

    const getRowIdentifier = (row: GridRowModel): string | undefined => {
        const rowId = row?.id;
        if (rowId !== undefined && rowId !== null) return String(rowId);
        const personId = row?.personReference?.id ?? row?._raw?.personReference?.id;
        if (personId) return String(personId);
        const rawId = row?._raw?.id;
        if (rawId !== undefined && rawId !== null) return String(rawId);
        return undefined;
    };

    const handleDetails = openMarketplaceAndFetchPerson(getPersonDetail, setIsMarketplaceOpen, handleItemClick);

    const isRowValid = useCallback(
        (id: string) => {
            // Row is valid if there's no email error
            return !errorInEmail.error;
        },
        [errorInEmail.error]
    );

    return (
        <V
            sx={{
                height: '100%',
                ...sx,
                '& .Mui-error': {
                    backgroundColor: `rgb(126,10,15, ${theme.palette.mode === 'dark' ? 0 : 0.1})`,
                    color: theme.palette.mode === 'dark' ? '#ff4343' : '#750f0f'
                },
                '& .Mui-error-row': {
                    backgroundColor: `rgb(126,10,15, ${theme.palette.mode === 'dark' ? 0 : 0.1})`,
                    color: theme.palette.mode === 'dark' ? '#ff4343' : '#750f0f',
                    '&.Mui-hovered  > * > [class|="MuiDataGrid-actionsCell"]': {
                        visibility: 'visible'
                    }
                },
                '& .Mui-pending-import-row': {
                    fontStyle: 'italic',
                    '&.Mui-hovered  > * > [class|="MuiDataGrid-actionsCell"]': {
                        visibility: 'visible'
                    }
                }
            }}
        >
            <DataGrid
                id={dataGridId}
                errors={errors}
                defaultColumnVisibility={columnVisibility}
                // TODO This is old details editing, should be a separate action (called from ... menu)
                onEdit={
                    canOpenContactEditor
                        ? (id, row) => {
                              setContactToEdit(row);
                              setContactEditorVisible(true);
                          }
                        : undefined
                }
                asIsColumns={errorInRow ? [errorInRow.field] : []}
                handleRowAction={showSaveButton ? undefined : handleRowAction}
                feature={feature}
                entityName={entityName}
                rowCount={rows ? rows.length : 0}
                rows={rows}
                setRows={setRows}
                columns={updatedColumns}
                serverPagination={false}
                filterModel={filterModel as GridFilterModel}
                disableColumnFilter={!!filterModel}
                clickableColumn={clickableColumn}
                isCellClickable={isCellClickable}
                toolbar={{
                    ...toolbar,
                    custom: toolbar.custom ? (showSaveButton ? [uploadButton, saveButton] : [uploadButton]) : undefined
                }}
                toolbarOptions={{
                    csvOptions: {
                        fileName: exportFilename
                    }
                }}
                processRowUpdate={processRowUpdate}
                enableInlineEditing={true}
                isRowValid={isRowValid}
                onRowEditStop={handleRowEditStop}
                getRowClassName={(params) => {
                    const id = getRowIdentifier(params.row);
                    const showErrorInRow = !!errorInRow && errorInRow?.id === id;
                    const isSavingCurrentRow = isSaving && !!id && !!currentRow && id === getRowIdentifier(currentRow);
                    const isDeleting = params.row.deleted;
                    const isPendingImportRow = id ? pendingImportRowIds.includes(id) : false;
                    const isDuplicatePersonError =
                        showErrorInRow && errorInRow?.message?.props?.token === 'reservation.guest.error.message';
                    if (isSavingCurrentRow || isDeleting) {
                        return 'Mui-row';
                    }
                    if (showErrorInRow && !isDuplicatePersonError) {
                        return 'Mui-error-row';
                    }
                    if (isPendingImportRow) {
                        return 'Mui-pending-import-row';
                    } else {
                        return 'tableRow';
                    }
                }}
                onDeleteHandle={onDeleteContact}
                onDetails={(index, row) => handleDetails(row?.personReference)}
                externalAddTriggerValue={externalAddTriggerValue}
                onCreateNewRow={onCreateNewRow}
                {...rest}
            />

            {contactEditorVisible ? (
                <ContactEditor
                    title={addTitle}
                    isBusy={saving}
                    contact={contactToEdit?._raw}
                    onEditContact={(contact) => {
                        setSaving(true);
                        Promise.resolve(onEditContact(contact, contactToEdit?.id))
                            .then(() => {
                                setSaving(false);
                                setContactEditorVisible(false);
                            })
                            .catch(() => {
                                setSaving(false);
                            });
                    }}
                    onClose={() => {
                        setContactEditorVisible(false);
                    }}
                />
            ) : (
                <React.Fragment />
            )}
        </V>
    );
}
